import { Box } from '#/components/Box/Box';
import type { OrgFormValues } from '#/components/Form/Fields/Org/getOrgFields';
import { Form } from '#/components/Form/Form';
import type {
  FormMapErrorFunction,
  FormSubmitFunction,
} from '#/components/Form/FormContext';
import type { MultiStepFormValues } from '#/components/Form/MultiStepForm/MultiStepFormTypes';
import { Icon } from '#/components/Icon/Icon';
import type { IconName } from '#/components/Icon/IconName';
import { OrgForm } from '#/components/OrgForm/OrgForm';
import { H5 } from '#/components/Text/H5';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { I18nFn } from '#/i18n/types/I18nFn';
import { colors } from '#/theme/theme';
import { duplicateNameError } from '#/utils/orgs';

export type CreateOrgPageFormValues = OrgFormValues & MultiStepFormValues;

function createSteps(i18n: I18nFn) {
  return [
    {
      icon: 'person-add',
      text: i18n('Set up your free organization account'),
    },
    {
      icon: 'cursor-click',
      text: i18n('Submit it to Idealist for a brief review'),
    },
    {
      icon: 'paper-upload',
      text: i18n('Post listings to find the best people for your organization'),
    },
  ] satisfies { icon: IconName; text: string }[];
}

function Intro() {
  const { i18n } = useI18n();

  return (
    <Box display={['block', null, 'flex']} my={[32, null, 64]}>
      {createSteps(i18n).map((step, index) => (
        <Box
          key={step.icon}
          width={[1, null, 1 / 3]}
          flex="0 0 auto"
          px={[0, 16, 32]}
          py={[24, null, 0]}
          // @ts-expect-error TS(2322): Type 'null' is not assignable to type 'BorderLeft<... Remove this comment to see the full error message
          borderLeft={[0, null, index > 0 && `1px solid ${colors.elementGrey}`]}
          // @ts-expect-error TS(2322): Type 'string | false' is not assignable to type 'B... Remove this comment to see the full error message
          borderTop={[index > 0 && `1px solid ${colors.elementGrey}`, null, 0]}
          display="flex"
          flexDirection={['row', null, 'column']}
          alignItems="center"
          textAlign={['left', null, 'center']}
        >
          <Box
            width={[64, null, 80]}
            height={[64, null, 80]}
            flex="0 0 auto"
            mt={[0, null, -20]}
            display="flex"
            justifyContent="center"
            alignItems="center"
            borderRadius="50%"
            bg={colors.calloutGrey}
          >
            <Icon name={step.icon} colorName="lightContentGrey" size={30} />
          </Box>

          <H5 mt={[0, null, 24]} mb={[0, null, -20]} pl={[16, null, 0]}>
            {index + 1}. {step.text}
          </H5>
        </Box>
      ))}
    </Box>
  );
}

type Props = {
  initialValues: Partial<CreateOrgPageFormValues>;
  onSubmit: FormSubmitFunction<CreateOrgPageFormValues>;
};

export function CreateOrgPage({ initialValues, onSubmit }: Props) {
  const { i18n, locale } = useI18n();

  const mapError: FormMapErrorFunction<CreateOrgPageFormValues> = ({
    name,
    type,
    description,
    data,
  }) => {
    if (name === 'name' && type === 'alreadyInUse') {
      // @ts-expect-error TS(2532): Object is possibly 'undefined'.
      return duplicateNameError(i18n, locale, data.org);
    }

    if (
      name === 'orgType' &&
      description ===
        '"CORPORATION" is not one of GOVERNMENT, NONPROFIT, CONSULTANT, RECRUITER'
    ) {
      return i18n('Business must be in the U.S.');
    }

    return description;
  };

  const trackingTitle = 'Create a New Organization Profile';
  const title =
    /* TRANSLATORS: Create org page title */
    i18n('Create a New Organization Profile');

  return (
    <Box pt={130}>
      <Form
        analyticsTitle="[userDashboard] Create org page"
        usageContext="page"
        qaId="create-org-form"
        initialValues={initialValues}
        mapError={mapError}
        onSubmit={onSubmit}
        render={() => (
          <OrgForm
            trackingTitle={trackingTitle}
            title={title}
            intro={<Intro />}
            submitText={i18n('Create & Preview')}
            simple
          />
        )}
      />
    </Box>
  );
}
